import { puckTemplateConfig } from './puckTemplateConfig';

/**
 * Le squelette d'un site : menu, pied de page, page de login, page de
 * chargement. Chaque section est un puckData d'un seul bloc.
 *
 * Ce module est la source unique des valeurs par défaut et des normalisations
 * de ces quatre sections, partagée par l'éditeur de template (où le squelette
 * vit dans ses colonnes) et par le document (où il est copié dans
 * `web_data.skeleton` à la génération — le template n'est plus qu'un modèle).
 */

/** Scope autosave/collab d'une section → type de bloc Puck qu'elle contient. */
export const SKELETON_BLOCK_TYPES = {
  header: 'SiteHeader',
  footer: 'SiteFooter',
  login: 'LoginPage',
  loading: 'LoadingPage',
};

export const SKELETON_SCOPES = Object.keys(SKELETON_BLOCK_TYPES);

/** Scope → colonne du template web qui le porte côté serveur. */
export const SKELETON_TEMPLATE_COLUMNS = {
  header: 'header_puck_data',
  footer: 'footer_puck_data',
  login: 'login_page_puck_data',
  loading: 'loading_page_puck_data',
};

const EMPTY_PUCK = { content: [], root: {} };

/* Box par défaut d'une section : un unique bloc pré-rempli avec les
   defaultProps de sa config Puck. Les sections ne sont jamais vides — leur
   éditeur verrouille le bloc (non supprimable, palette masquée). */
const seedGlobalSection = (type) => ({
  root: {},
  content: [{
    type,
    props: {
      id: `${type}-1`,
      ...(puckTemplateConfig.components[type]?.defaultProps || {}),
    },
  }],
});

const DEFAULT_LOADING_PUCK = {
  content: [{
    type: 'LoadingPage',
    props: {
      id: 'loading-1',
      backgroundColor: '#0f172a',
      mediaKind: 'spinner',
      mediaUrlImage: '',
      mediaUrlVideo: '',
      mediaWidth: '12.5rem',
      spinnerType: 'oval',
      spinnerColor: '',
      spinnerSize: '3rem',
      title: '',
      subtitle: '',
      durationSeconds: 3,
      endMode: 'duration',
      videoLoops: 1,
      fadeType: 'fade',
    },
  }],
  root: {},
};

export const DEFAULT_SKELETON_SECTIONS = {
  header: seedGlobalSection('SiteHeader'),
  footer: seedGlobalSection('SiteFooter'),
  login: seedGlobalSection('LoginPage'),
  loading: DEFAULT_LOADING_PUCK,
};

/** Rend une box si la section est vide (content absent/vide). */
const withSeed = (data, seed) => (data?.content?.length ? data : seed);

/**
 * Comble les props manquantes (section enregistrée avant l'ajout d'un champ)
 * avec les defaultProps déclarés du composant — jamais l'inverse, les valeurs
 * déjà enregistrées gagnent toujours. Pas un adaptateur de forme legacy : juste
 * éviter des champs vides par défaut dans la sidebar.
 */
const backfillLoadingDefaults = (data) => {
  const item = data.content[0];
  if (!item) return data;
  const defaults = puckTemplateConfig.components.LoadingPage?.defaultProps || {};
  return {
    ...data,
    content: [{ ...item, props: { ...defaults, ...item.props } }],
  };
};

/** Un menu et un seul : le dernier gagne (un drop en trop passait inaperçu). */
export const normalizeHeaderSection = (data) => {
  if (!data || typeof data !== 'object') return EMPTY_PUCK;
  const content = Array.isArray(data.content)
    ? data.content.filter((c) => c?.type === 'SiteHeader')
    : [];
  return {
    ...data,
    content: content.length > 0 ? [content[content.length - 1]] : [],
  };
};

/**
 * Données affichables d'une section : jamais vides, jamais dédoublées, props
 * manquantes comblées. Point de passage obligé au chargement et à la copie.
 */
export function normalizeSkeletonSection(scope, data) {
  const seed = DEFAULT_SKELETON_SECTIONS[scope];
  if (!seed) return data;
  if (scope === 'header') return withSeed(normalizeHeaderSection(data), seed);
  if (scope === 'loading') {
    return data?.content?.length ? backfillLoadingDefaults(data) : seed;
  }
  return withSeed(data, seed);
}

/**
 * Copie le squelette d'un template pour un document. C'est une copie : le
 * document ne relit plus le template ensuite (un template modifié après coup
 * ne change rien aux documents déjà générés).
 */
export function buildSkeletonFromTemplate(webTemplate) {
  return Object.fromEntries(SKELETON_SCOPES.map((scope) => [
    scope,
    normalizeSkeletonSection(scope, webTemplate?.[SKELETON_TEMPLATE_COLUMNS[scope]]),
  ]));
}

/**
 * Section à afficher pour un site : celle du document, sinon celle de son
 * template. Le repli ne sert qu'aux documents générés avant que la copie
 * existe — un document à jour ne lit plus son template.
 */
export function resolveSkeletonSection(webData, webTemplate, scope) {
  const own = webData?.skeleton?.[scope];
  if (own?.content?.length) return own;
  return webTemplate?.[SKELETON_TEMPLATE_COLUMNS[scope]];
}

/**
 * Complète le squelette d'un document généré avant que la copie existe : les
 * sections manquantes sont semées depuis le template, celles déjà présentes ne
 * sont jamais touchées. Retourne la même référence si rien ne manque.
 */
export function withSkeletonFromTemplate(webData, webTemplate) {
  const current = webData?.skeleton || {};
  const missing = SKELETON_SCOPES.filter((scope) => !current[scope]?.content?.length);
  if (!missing.length) return webData;

  const fromTemplate = buildSkeletonFromTemplate(webTemplate);
  return {
    ...webData,
    skeleton: {
      ...current,
      ...Object.fromEntries(missing.map((scope) => [scope, fromTemplate[scope]])),
    },
  };
}
